/**
 * 简化的国际化工具类
 * 管理语言切换、本地存储和浏览器语言检测
 */

import { nextTick } from 'vue'

// 支持的语言类型
export type SupportedLocale = 'en' | 'zh'

// 支持的语言列表
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'zh'] as const

// 语言信息配置
export const LOCALE_INFO: Record<SupportedLocale, {
  name: string
  nativeName: string
  flag: string
  rtl: boolean
}> = {
  en: {
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    rtl: false
  },
  zh: {
    name: 'Chinese',
    nativeName: '中文',
    flag: '🇨🇳',
    rtl: false
  }
}

// 本地存储键名
const LOCALE_STORAGE_KEY = 'carbon-trading-locale'

/**
 * 获取浏览器首选语言
 */
export function getBrowserLocale(): SupportedLocale {
  try {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return 'en'
    }
    
    const browserLang = navigator.language.toLowerCase()
    
    // 检查完全匹配
    if (SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
      return browserLang as SupportedLocale
    }
    
    // 检查语言代码匹配（如 zh-CN -> zh）
    const langCode = browserLang.split('-')[0]
    if (SUPPORTED_LOCALES.includes(langCode as SupportedLocale)) {
      return langCode as SupportedLocale
    }
    
    return 'en'
  } catch (error) {
    console.warn('Failed to get browser locale:', error)
    return 'en'
  }
}

/**
 * 从本地存储获取保存的语言
 */
export function getStoredLocale(): SupportedLocale | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null
    }
    
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && SUPPORTED_LOCALES.includes(stored as SupportedLocale)) {
      return stored as SupportedLocale
    }
  } catch (error) {
    console.warn('Failed to get stored locale:', error)
  }
  return null
}

/**
 * 保存语言到本地存储
 */
export function setStoredLocale(locale: SupportedLocale): void {
  try {
    if (typeof localStorage === 'undefined') {
      return
    }
    
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch (error) {
    console.warn('Failed to store locale:', error)
  }
}

/**
 * 获取初始语言
 */
export function getInitialLocale(): SupportedLocale {
  const stored = getStoredLocale()
  if (stored) {
    return stored
  }
  
  return getBrowserLocale()
}

/**
 * 切换语言
 */
export async function switchLocale(i18n: { global: { locale: { value: string } } }, locale: SupportedLocale): Promise<void> {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    console.warn(`Unsupported locale: ${locale}`)
    return
  }
  
  try {
    // 设置 i18n 语言
    i18n.global.locale.value = locale
    
    // 保存到本地存储
    setStoredLocale(locale)
    
    // 设置 HTML 属性
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
      document.documentElement.dir = LOCALE_INFO[locale].rtl ? 'rtl' : 'ltr'
    }
    
    // 等待 DOM 更新
    await nextTick()
    
    console.log(`Language switched to: ${locale}`)
  } catch (error) {
    console.error('Failed to switch locale:', error)
    throw error
  }
}

/**
 * 初始化国际化
 */
export function initializeI18n(i18n: { global: { locale: { value: string } } }): void {
  try {
    const initialLocale = getInitialLocale()
    
    // 设置初始语言
    i18n.global.locale.value = initialLocale
    
    // 设置 HTML 属性
    if (typeof document !== 'undefined') {
      document.documentElement.lang = initialLocale
      document.documentElement.dir = LOCALE_INFO[initialLocale].rtl ? 'rtl' : 'ltr'
    }
    
    console.log(`I18n initialized with locale: ${initialLocale}`)
  } catch (error) {
    console.error('Failed to initialize i18n:', error)
    // 回退到默认英文
    if (typeof document !== 'undefined') {
      document.documentElement.lang = 'en'
      document.documentElement.dir = 'ltr'
    }
  }
}

/**
 * 获取当前语言信息
 */
export function getCurrentLocaleInfo(locale: SupportedLocale) {
  return LOCALE_INFO[locale]
}

/**
 * 检查是否为 RTL 语言
 */
export function isRTL(locale: SupportedLocale): boolean {
  return LOCALE_INFO[locale].rtl
}

/**
 * 获取语言切换选项
 */
export function getLocaleOptions() {
  return SUPPORTED_LOCALES.map(locale => ({
    value: locale,
    label: LOCALE_INFO[locale].nativeName,
    flag: LOCALE_INFO[locale].flag
  }))
}

/**
 * 格式化数字（根据语言环境）
 */
export function formatNumber(
  value: number,
  locale: SupportedLocale,
  options?: Intl.NumberFormatOptions
): string {
  try {
    return new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : 'en-US', options).format(value)
  } catch (error) {
    console.warn('Failed to format number:', error)
    return value.toString()
  }
}

/**
 * 格式化货币（根据语言环境）
 */
export function formatCurrency(
  value: number,
  locale: SupportedLocale,
  currency: string = 'USD'
): string {
  try {
    return new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : 'en-US', {
      style: 'currency',
      currency: currency
    }).format(value)
  } catch (error) {
    console.warn('Failed to format currency:', error)
    return `${currency} ${value}`
  }
}

/**
 * 格式化日期（根据语言环境）
 */
export function formatDate(
  date: Date | string | number,
  locale: SupportedLocale,
  options?: Intl.DateTimeFormatOptions
): string {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date
    return new Intl.DateTimeFormat(locale === 'zh' ? 'zh-CN' : 'en-US', options).format(dateObj)
  } catch (error) {
    console.warn('Failed to format date:', error)
    return date.toString()
  }
}